import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Alert,
} from '@mui/material';
import type { ExecuteEntity, PlanEntity, FlatExecuteStep, ExecuteCheckpointDef } from '@/types/run-stage.types';
import {
  executeStartStep,
  executeCompleteStep,
  executeSkipStep,
  executeRepeatStep,
  executeFinishRun,
  executeSkipStepV2,
  executeRepeatStepV2,
} from '@services/runApi.service';
import {
  updateExecuteStep,
  completeExecuteStepV2,
} from '@services/runStageApi.service';
import { useRunStageStore } from '@/run-stage/store/runStageStore';
import { useExecuteStepStore } from '@/run-stage/store/executeStepStore';
import { buildEdgeMap, buildVertexMap } from '@/run-stage/hooks/useExecuteTranslations';
import { RunStatusBar } from './RunStatusBar';
import { StepNavigator } from './StepNavigator';
import { StepStatsRow } from './StepStatsRow';
import { StepDetailCard } from './StepDetailCard';
import { ExecuteSidebar } from './ExecuteSidebar';
import { BottomActionBar } from './BottomActionBar';

interface ExecuteStepRunnerProps {
  execute: ExecuteEntity;
  plan?: PlanEntity | null;
  isReadOnly?: boolean;
  isOffline?: boolean;
  /** Called after run is successfully finished (for stepper advance) */
  onRunFinished?: () => void;
}

/**
 * Validate required checkpoints. Returns a map of checkpointId → error message.
 */
function validateCheckpoints(
  checkpoints: ExecuteCheckpointDef[],
  dirtyValues: Map<string, { value?: unknown }>
): Map<string, string> {
  const errors = new Map<string, string>();

  for (const cp of checkpoints) {
    if (!cp.required) continue;
    const id = cp.id || cp._id || '';
    const dirty = dirtyValues.get(id);
    const value = dirty?.value;

    if (value == null || value === '') {
      errors.set(id, 'This field is required');
      continue;
    }

    // Range validation
    if (cp.fieldType === 'range' && typeof value === 'object' && value !== null) {
      const rv = value as { min?: number | null; max?: number | null };
      if (cp.rangeMin != null && rv.min != null && rv.min < cp.rangeMin) {
        errors.set(id, `Min must be ≥ ${cp.rangeMin}`);
      }
      if (cp.rangeMax != null && rv.max != null && rv.max > cp.rangeMax) {
        errors.set(id, `Max must be ≤ ${cp.rangeMax}`);
      }
    }

    // Number range validation
    if (cp.fieldType === 'number' && typeof value === 'number') {
      if (cp.rangeMin != null && value < cp.rangeMin) {
        errors.set(id, `Value must be ≥ ${cp.rangeMin}`);
      }
      if (cp.rangeMax != null && value > cp.rangeMax) {
        errors.set(id, `Value must be ≤ ${cp.rangeMax}`);
      }
    }

    // Recurse into group children
    if (cp.fieldType === 'group' && cp.children) {
      const childErrors = validateCheckpoints(cp.children, dirtyValues);
      childErrors.forEach((msg, cid) => errors.set(cid, msg));
    }
  }

  return errors;
}

function flattenCheckpointDefs(checkpoints: ExecuteCheckpointDef[] = []): ExecuteCheckpointDef[] {
  const result: ExecuteCheckpointDef[] = [];
  for (const checkpoint of checkpoints) {
    result.push(checkpoint);
    if (checkpoint.fieldType === 'group' && checkpoint.children?.length) {
      result.push(...flattenCheckpointDefs(checkpoint.children));
    }
  }
  return result;
}

export const ExecuteStepRunner: React.FC<ExecuteStepRunnerProps> = ({
  execute,
  plan,
  isReadOnly = false,
  isOffline = false,
  onRunFinished,
}) => {
  const refresh = useRunStageStore((s) => s.refresh);
  const run = useRunStageStore((s) => s.context?.run);
  // Graph edges + vertices from the store (populated during bootstrap)
  const planGraphEdges = useRunStageStore((s) => s.context?.planGraphEdges);
  const executeGraphEdges = useRunStageStore((s) => s.context?.executeGraphEdges);
  const planGraphVertices = useRunStageStore((s) => s.context?.planGraphVertices);
  const executeGraphVertices = useRunStageStore((s) => s.context?.executeGraphVertices);

  // Build edge maps (v1 mxGraph runs: operations ARE edges)
  const planEdgeMap = useMemo(() => buildEdgeMap(planGraphEdges), [planGraphEdges]);
  const executeEdgeMap = useMemo(() => buildEdgeMap(executeGraphEdges), [executeGraphEdges]);

  // Build vertex maps (v2 maxGraph / operationsInNodes runs: operations ARE vertices)
  const planVertexMap = useMemo(() => buildVertexMap(planGraphVertices), [planGraphVertices]);
  const executeVertexMap = useMemo(() => buildVertexMap(executeGraphVertices), [executeGraphVertices]);

  // Track offline timing overrides: stepId → { startTime, endTime }
  const offlineTimingsRef = useRef<Map<string, { startTime?: string; endTime?: string }>>(new Map());

  const handleTimingChange = useCallback(
    (stepId: string, field: 'startTime' | 'endTime', value: string) => {
      const map = offlineTimingsRef.current;
      const existing = map.get(stepId) ?? {};
      map.set(stepId, { ...existing, [field]: value });
    },
    [],
  );

  // Execute step store
  const flatSteps = useExecuteStepStore((s) => s.flatSteps);
  const currentIndex = useExecuteStepStore((s) => s.currentIndex);
  const stats = useExecuteStepStore((s) => s.stats);
  const isSaving = useExecuteStepStore((s) => s.isSaving);
  const error = useExecuteStepStore((s) => s.error);
  const dirtyCheckpoints = useExecuteStepStore((s) => s.dirtyCheckpoints);
  const checkpointErrors = useExecuteStepStore((s) => s.checkpointErrors);
  const init = useExecuteStepStore((s) => s.init);
  const goToStep = useExecuteStepStore((s) => s.goToStep);
  const prevStep = useExecuteStepStore((s) => s.prevStep);
  const nextStep = useExecuteStepStore((s) => s.nextStep);
  const goToActiveStep = useExecuteStepStore((s) => s.goToActiveStep);
  const setSaving = useExecuteStepStore((s) => s.setSaving);
  const setError = useExecuteStepStore((s) => s.setError);

  // Initialize flat steps from execute + plan
  useEffect(() => {
    init(execute, plan ?? null);
  }, [execute, plan, init]);

  const currentStep: FlatExecuteStep | undefined = flatSteps[currentIndex];
  const runId = execute.runId;
  const runUid = (execute as unknown as Record<string, unknown>).runUid as string | undefined;
  const useV2CompleteApi =
    Boolean(run?.operationsInNodes) ||
    run?.sourceEditor === 'maxgraph' ||
    (run?.runStageSchemaVersion ?? 0) >= 2;

  const allStepsDone = stats.pending === 0 && stats.inProgress === 0 && stats.total > 0;
  const hasCheckpointErrors = checkpointErrors.size > 0;

  // ── Actions ──

  const handleStartAndComplete = useCallback(async () => {
    if (!currentStep || !runId) return;
    const step = currentStep.step;
    const offlineTiming = isOffline ? offlineTimingsRef.current.get(step.stepId) : undefined;
    setSaving(true);
    setError(null);
    try {
      // If step hasn't started, start it first
      if (!useV2CompleteApi && step.status !== 'IN_PROGRESS') {
        await executeStartStep({
          runId,
          runUid,
          stepId: step.stepId,
          cloneNumber: step.cloneNumber ?? 0,
          ...(offlineTiming?.startTime ? { startTime: offlineTiming.startTime } : {}),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start step');
      setSaving(false);
      return;
    }

    // Now complete
    try {
      // Validate checkpoints before completing
      if (currentStep.checkpoints && currentStep.checkpoints.length > 0) {
        const errors = validateCheckpoints(currentStep.checkpoints, dirtyCheckpoints);
        if (errors.size > 0) {
          useExecuteStepStore.setState({ checkpointErrors: errors });
          setSaving(false);
          return;
        }
      }

      if (useV2CompleteApi) {
        const checkpointDefById = new Map(
          flattenCheckpointDefs(currentStep.checkpoints ?? []).map((cp) => [cp.id || cp._id || '', cp]),
        );
        const checkpointResponses = Array.from(dirtyCheckpoints.values()).map((dirty) => {
          const cp = checkpointDefById.get(dirty.checkpointId);
          return {
            checkpointId: dirty.checkpointId,
            checkpointName: cp?.checkpointName,
            fieldType: cp?.fieldType,
            value: dirty.value,
          };
        });

        const startTime =
          offlineTiming?.startTime ||
          step.realTime?.startTime ||
          new Date().toISOString();
        const endTime = offlineTiming?.endTime || new Date().toISOString();
        const durationMs = Math.max(0, new Date(endTime).getTime() - new Date(startTime).getTime());

        await completeExecuteStepV2({
          runId,
          stepId: step.stepId,
          payload: {
            status: 'COMPLETED',
            realTime: {
              startTime,
              endTime,
              duration: String(durationMs),
              source: isOffline ? 'manual' : 'system',
            },
            checkpointResponses,
            timings: [{ startTime, endTime }],
          },
        });
      } else if (isOffline && offlineTiming?.endTime) {
        // For offline mode, send the endTime with the legacy update path.
        await updateExecuteStep({
          runId,
          stepId: step.stepId,
          cloneNumber: step.cloneNumber ?? 0,
          endTime: offlineTiming.endTime,
          status: 'COMPLETED',
          completed: true,
        });
      } else {
        await executeCompleteStep({
          runId,
          runUid,
          stepId: step.stepId,
          cloneNumber: step.cloneNumber ?? 0,
        });
      }
      // Clear this step's offline timing after save
      offlineTimingsRef.current.delete(step.stepId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete step');
    } finally {
      setSaving(false);
    }
  }, [
    currentStep,
    runId,
    runUid,
    isOffline,
    dirtyCheckpoints,
    setSaving,
    setError,
    refresh,
    useV2CompleteApi,
  ]);

  const handleSkip = useCallback(async () => {
    if (!currentStep || !runId) return;
    setSaving(true);
    setError(null);
    try {
      if (useV2CompleteApi) {
        await executeSkipStepV2({
          runId,
          stepId: currentStep.step.stepId,
        });
      } else {
        await executeSkipStep({
          runId,
          runUid,
          stepId: currentStep.step.stepId,
          cloneNumber: currentStep.step.cloneNumber ?? 0,
        });
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to skip step');
    } finally {
      setSaving(false);
    }
  }, [currentStep, runId, runUid, setSaving, setError, refresh, useV2CompleteApi]);

  const handleRepeat = useCallback(async () => {
    if (!currentStep || !runId) return;
    setSaving(true);
    setError(null);
    try {
      if (useV2CompleteApi) {
        const result = await executeRepeatStepV2({
          runId,
          stepId: currentStep.step.stepId,
          cloneNumber: currentStep.step.cloneNumber ?? 1,
        });
        // Insert repeated step into store after current index
        if (result?.data?.repeatedStep) {
          const { flatSteps } = useExecuteStepStore.getState();
          const newFlatStep = {
            step: {
              stepId: (result.data.repeatedStep as { stepId: string }).stepId,
              stepName: (result.data.repeatedStep as { displayName: string }).displayName,
              status: '',
              completed: false,
              cloneNumber: currentStep.step.cloneNumber ?? 1,
              isRepeated: true,
              repeatParentId: currentStep.step.stepId,
            } as FlatExecuteStep['step'],
            checkpoints: currentStep.checkpoints,
          } as FlatExecuteStep;
          const updated = [...flatSteps];
          updated.splice(currentIndex + 1, 0, newFlatStep);
          useExecuteStepStore.setState({ flatSteps: updated });
        }
      } else {
        await executeRepeatStep({
          runId,
          runUid,
          stepId: currentStep.step.stepId,
          cloneNumber: currentStep.step.cloneNumber ?? 0,
        });
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to repeat step');
    } finally {
      setSaving(false);
    }
  }, [currentStep, currentIndex, runId, runUid, setSaving, setError, refresh, useV2CompleteApi]);

  const handleFinish = useCallback(async () => {
    if (!runId) return;
    // If the current step is not yet completed, complete it first
    if (currentStep && !currentStep.step.completed && currentStep.step.status !== 'COMPLETED') {
      await handleStartAndComplete();
      // Check if completion succeeded
      const { error: completionError } = useExecuteStepStore.getState();
      if (completionError) return;
    }
    setSaving(true);
    setError(null);
    try {
      await executeFinishRun({ runId, runUid });
      await refresh();
      onRunFinished?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to finish run');
    } finally {
      setSaving(false);
    }
  }, [runId, runUid, currentStep, handleStartAndComplete, setSaving, setError, refresh, onRunFinished]);

  // ── Render ──

  if (!flatSteps.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No execute steps found for this run.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Run status bar */}
      <RunStatusBar execute={execute} stats={stats} />

      {/* Step navigator (pills + prev/next) */}
      <StepNavigator
        flatSteps={flatSteps}
        currentIndex={currentIndex}
        onGoToStep={goToStep}
        onPrev={prevStep}
        onNext={nextStep}
        onGoToActiveStep={goToActiveStep}
        disabled={isSaving}
      />

      {/* Stats row */}
      <StepStatsRow stats={stats} />

      {/* Error banner */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main content: step detail + sidebar */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Step detail (80%) */}
        <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
          {currentStep && (
            <StepDetailCard
              flatStep={currentStep}
              allFlatSteps={flatSteps}
              currentIndex={currentIndex}
              isReadOnly={isReadOnly}
              isOffline={isOffline}
              onTimingChange={handleTimingChange}
              executeEdgeMap={executeEdgeMap}
              planEdgeMap={planEdgeMap}
              executeVertexMap={executeVertexMap}
              planVertexMap={planVertexMap}
              runId={execute.runId}
              onParamSaved={refresh}
            />
          )}
        </Box>

        {/* Sidebar (icon strip + panel) */}
        <ExecuteSidebar currentStep={currentStep} isReadOnly={isReadOnly} />
      </Box>

      {/* Bottom action bar */}
      <BottomActionBar
        currentStep={currentStep}
        isSaving={isSaving}
        isReadOnly={isReadOnly}
        allStepsDone={allStepsDone}
        hasCheckpointErrors={hasCheckpointErrors}
        isLastStep={currentIndex === flatSteps.length - 1}
        onMarkComplete={handleStartAndComplete}
        onSkip={handleSkip}
        onRepeat={handleRepeat}
        onFinish={handleFinish}
      />
    </Box>
  );
};
